import PStorage, { ENCRYPTED_PRIVATE_KEY, PRIVATE_KEY, API_TOKEN } from '@/util/PStorage'
import Vue from 'vue'
import { UI_SET_LAST_URL } from '@/store/ui/constants'
import API from './api/API'

class Locker {
    /**
     * Reference to the current interval
     */
    private interval?: NodeJS.Timer

    /**
     * Duration between lock checks (seconds)
     */
    private intervalDuration: number = 1

    /**
     * Vue context for routing
     */
    private $vue?: Vue

    /**
     * Attempt to get the value of the private key or encrypted
     * private key without touching the storage entry. This will
     * trigger an expiry and return undefined if the user should be
     * logged out
     *
     * @returns true if still authenticated
     */
    public run($vue?: Vue): boolean {
        $vue = this.$vue || $vue
        if (!$vue) {
            return true
        }
        if (this.shouldRegister()) {
            $vue.$router.push('/register')
            return false
        }
        if (this.shouldUnlock() && $vue.$route.path !== '/unlock') {
            this.lock()
            return false
        }
        return true
    }

    /**
     * Check whether the user has an encrypted private key
     *
     * If not, they should be redirected to /register
     */
    public shouldRegister(): boolean {
        return PStorage.get(ENCRYPTED_PRIVATE_KEY, false) === undefined
    }

    /**
     * Check whether the user has an unencrypted private key
     *
     * If not, they should be redirected to /unlock
     */
    public shouldUnlock(): boolean {
        return PStorage.get(PRIVATE_KEY, false) === undefined
    }

    /**
     * Lock the application
     *
     * Used when the private key can't be access (has expired)
     */
    public lock(): void {
        if (this.$vue && this.$vue.$route.path !== '/unlock') {
            this.$vue.$store.commit(UI_SET_LAST_URL, this.$vue.$route.path)
            this.$vue.$router.push('/unlock')
            this.$vue.$notice({ text: 'Volt Locked' })
            PStorage.remove(API_TOKEN)
            API.setConnected(false)
        }
    }

    /**
     * Begin the locker interval, cancelling the previous
     * interval if one is already running.
     *
     * @param $vue Vue context
     */
    public beginInterval($vue: Vue): void {
        this.$vue = $vue
        this.endInterval()
        const run = () => this.run()
        this.interval = setInterval(run, this.intervalDuration * 1000)
        document.removeEventListener('visibilitychange', run)
        document.addEventListener('visibilitychange', run)
    }

    /**
     * Cancel the current interval if one is running.
     */
    public endInterval(): void {
        if (this.interval) {
            clearInterval(this.interval)
        }
    }
}

export default new Locker()
